import LogicFlow from '@logicflow/core';

/**
 * 节点布局配置
 */
interface LayoutConfig {
  nodeWidth: number;        // 节点宽度
  fieldHeight: number;      // 每个字段的高度
  nodeHeaderHeight: number; // 节点头部高度
  nodePadding: number;      // 节点内边距
  verticalGap: number;      // 节点之间的垂直间距
  horizontalGap: number;    // 节点之间的水平间距
  startX: number;           // 起始 X 坐标
  startY: number;           // 起始 Y 坐标
  columnsCount: number;     // 每行显示的节点数量
}

/**
 * 默认布局配置
 */
const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  nodeWidth: 200,        // 与 sqlNode.ts 中的 width 一致
  fieldHeight: 24,       // 与 sqlNode.ts 中的字段高度一致
  nodeHeaderHeight: 60,  // 与 sqlNode.ts 中的基础高度一致
  nodePadding: 0,        // sqlNode.ts 中没有额外内边距
  verticalGap: 60,
  horizontalGap: 100,
  startX: 100,
  startY: 100,
  columnsCount: 3,
};

/**
 * 计算节点高度
 * 
 * 必须与 sqlNode.ts 中的高度计算公式完全一致：
 * this.height = 60 + fields.length * 24;
 * 
 * @param fieldsCount 字段数量
 * @param config 布局配置
 */
export const calculateNodeHeight = (
  fieldsCount: number,
  config: LayoutConfig = DEFAULT_LAYOUT_CONFIG
): number => {
  const { fieldHeight, nodeHeaderHeight } = config;
  // 节点高度 = 60 + 字段数量 * 24（与 sqlNode.ts 完全一致）
  return nodeHeaderHeight + fieldHeight * fieldsCount;
};

/**
 * 计算节点位置（网格布局）
 * @param index 节点索引
 * @param fieldsCount 当前节点字段数量
 * @param allNodeFieldsCounts 所有节点的字段数量数组
 * @param config 布局配置
 */
export const calculateNodePosition = (
  index: number,
  fieldsCount: number,
  allNodeFieldsCounts: number[],
  config: LayoutConfig = DEFAULT_LAYOUT_CONFIG
): { x: number; y: number } => {
  const { startX, startY, nodeWidth, horizontalGap, verticalGap, columnsCount } = config;

  // 计算当前节点在第几行、第几列
  const row = Math.floor(index / columnsCount);
  const col = index % columnsCount;

  // 计算 X 坐标（列位置）
  const x = startX + col * (nodeWidth + horizontalGap);

  // 计算 Y 坐标（需要累加前面行的最大高度）
  let y = startY;
  
  // 累加前面所有行的最大高度
  for (let r = 0; r < row; r++) {
    // 找出当前行所有节点的最大高度
    const rowStartIndex = r * columnsCount;
    const rowEndIndex = Math.min(rowStartIndex + columnsCount, allNodeFieldsCounts.length);
    const rowNodeFieldsCounts = allNodeFieldsCounts.slice(rowStartIndex, rowEndIndex);
    
    const maxHeightInRow = Math.max(
      ...rowNodeFieldsCounts.map(count => calculateNodeHeight(count, config))
    );
    
    y += maxHeightInRow + verticalGap;
  }

  return { x, y };
};

/**
 * 自动布局所有节点
 * @param nodes 节点数组
 * @param config 布局配置
 */
export const autoLayoutNodes = (
  nodes: LogicFlow.NodeConfig[],
  config: Partial<LayoutConfig> = {}
): LogicFlow.NodeConfig[] => {
  const finalConfig = { ...DEFAULT_LAYOUT_CONFIG, ...config };
  
  // 提取所有节点的字段数量
  const allNodeFieldsCounts = nodes.map(node => {
    const fields = (node.properties as any)?.fields || [];
    return Array.isArray(fields) ? fields.length : 0;
  });

  // 为每个节点计算新位置
  return nodes.map((node, index) => {
    const fieldsCount = allNodeFieldsCounts[index];
    const position = calculateNodePosition(index, fieldsCount, allNodeFieldsCounts, finalConfig);
    
    return {
      ...node,
      x: position.x,
      y: position.y,
    };
  });
};

/**
 * 垂直单列布局（所有节点在左侧垂直排列）
 * 
 * LogicFlow 节点的 (x, y) 是节点的中心点坐标，不是左上角
 * 因此需要根据节点高度计算正确的中心点Y坐标
 * 
 * @param nodes 节点数组
 * @param config 布局配置
 */
export const verticalLayoutNodes = (
  nodes: LogicFlow.NodeConfig[],
  config: Partial<LayoutConfig> = {}
): LogicFlow.NodeConfig[] => {
  const finalConfig = { ...DEFAULT_LAYOUT_CONFIG, ...config };
  const { startX, startY, verticalGap } = finalConfig;
  
  // currentTopY 表示当前节点的顶部Y坐标
  let currentTopY = startY;
  
  return nodes.map((node) => {
    const fields = (node.properties as any)?.fields || [];
    const fieldsCount = Array.isArray(fields) ? fields.length : 0;
    const nodeHeight = calculateNodeHeight(fieldsCount, finalConfig);
    
    // 节点的Y坐标是中心点，所以 = 顶部Y + 高度的一半
    const position = {
      x: startX,
      y: currentTopY + nodeHeight / 2,
    };
    
    // 下一个节点的顶部Y = 当前节点顶部Y + 当前节点高度 + 间距
    currentTopY += nodeHeight + verticalGap;
    
    return {
      ...node,
      x: position.x,
      y: position.y,
    };
  });
};

/**
 * 计算本体节点的居中位置
 * @param dataSourceNodes 数据源节点数组
 * @param ontologyFieldsCount 本体节点字段数量
 * @param config 布局配置
 */
export const calculateOntologyPosition = (
  dataSourceNodes: LogicFlow.NodeConfig[],
  ontologyFieldsCount: number,
  config: Partial<LayoutConfig> = {}
): { x: number; y: number } => {
  const finalConfig = { ...DEFAULT_LAYOUT_CONFIG, ...config };
  
  if (dataSourceNodes.length === 0) {
    // 如果没有数据源节点，返回默认位置
    return { x: 800, y: 300 };
  }

  // 计算所有数据源节点的边界
  let minY = Infinity;
  let maxY = -Infinity;

  dataSourceNodes.forEach(node => {
    const fields = (node.properties as any)?.fields || [];
    const fieldsCount = Array.isArray(fields) ? fields.length : 0;
    const nodeHeight = calculateNodeHeight(fieldsCount, finalConfig);
    
    const nodeY = node.y || 0;
    minY = Math.min(minY, nodeY);
    maxY = Math.max(maxY, nodeY + nodeHeight);
  });

  // 计算本体节点高度
  const ontologyHeight = calculateNodeHeight(ontologyFieldsCount, finalConfig);
  
  // 本体节点垂直居中于所有数据源节点
  const centerY = (minY + maxY) / 2;
  const ontologyY = centerY - ontologyHeight / 2;

  // 本体节点放在右侧，距离数据源节点一定距离
  const maxX = Math.max(...dataSourceNodes.map(n => n.x || 0));
  const ontologyX = maxX + finalConfig.nodeWidth + finalConfig.horizontalGap * 2;

  return { x: ontologyX, y: ontologyY };
};

/**
 * 检测节点是否重叠
 * @param nodes 节点数组
 * @param config 布局配置
 */
export const detectNodeOverlap = (
  nodes: LogicFlow.NodeConfig[],
  config: Partial<LayoutConfig> = {}
): boolean => {
  const finalConfig = { ...DEFAULT_LAYOUT_CONFIG, ...config };
  
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const node1 = nodes[i];
      const node2 = nodes[j];
      
      const fields1 = (node1.properties as any)?.fields || [];
      const fields2 = (node2.properties as any)?.fields || [];
      const height1 = calculateNodeHeight(fields1.length, finalConfig);
      const height2 = calculateNodeHeight(fields2.length, finalConfig);
      
      const x1 = node1.x || 0;
      const y1 = node1.y || 0;
      const x2 = node2.x || 0;
      const y2 = node2.y || 0;
      
      // 检测矩形重叠
      const overlapX = Math.abs(x1 - x2) < finalConfig.nodeWidth;
      const overlapY = Math.abs(y1 - y2) < Math.max(height1, height2);
      
      if (overlapX && overlapY) {
        return true;
      }
    }
  }
  
  return false;
};
